import { Box, Stack, Typography } from '@mui/material';
import { useListContext, useTranslate } from 'react-admin';
import { BarChartWithTrend } from '@/components/analytics/common/BarChartWithTrend.jsx';
import { calcGrowth, prepareTrendChartData } from '@/helpers/analytics/charts.js';
import { formatNumberWithComma } from '@/helpers/format/formatNumber.js';
import { Growth } from '@/components/analytics/common/Growth.jsx';
import { ChartAnalyticsCard } from '@/components/analytics/common/ChartAnalyticsCard.jsx';
import { styled } from '@mui/material/styles';
import { NoDataPlaceholder } from '@/components/analytics/common/NoDataPlaceholder.jsx';
import { analyticsStyles } from '@/themes/commonStyles/analytics.js';

const StyledSubtitleBox = styled(Box)(() => ({
    ...analyticsStyles.analyticsChartSubtitle,
    '& .MuiTypography-root.MuiTypography-h5': {
        fontWeight: '400',
    },
}));

const Title = ({ value, growth }) => {
    const translate = useTranslate();
    return (
        <Stack spacing={1} sx={{ mb: 4 }}>
            <Typography variant='h6'>{translate('analytics.product.collectedProducts')}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant='h3'>{value}</Typography>
                <Growth value={growth} />
            </Box>
        </Stack>
    );
};

const Subtitle = ({ value, caption }) => (
    <StyledSubtitleBox className='analytics-chart-subtitle'>
        <Typography variant='caption'>{caption}</Typography>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            {value ? <Typography variant='h5'>{formatNumberWithComma(value, 1)}</Typography> : <NoDataPlaceholder width='94px' height='28px' />}
        </Box>
    </StyledSubtitleBox>
);

export const ProductCountAnalytics = () => {
    const translate = useTranslate();
    const { data } = useListContext();

    if (!data || data.length === 0) return null;

    const analytic = data.find(s => s.id === 'current').data;
    const device = data.find(s => s.id === 'device').data;
    const previous = data.find(s => s.id === 'previous').data;
    const series = data.find(s => s.id === 'series').data;
    const chartData = prepareTrendChartData(series, 'productsCount');

    return (
        <ChartAnalyticsCard
            data={chartData}
            dataKey='productsCount'
            className='product-count-analytics'
            stats={
                <>
                    <Title value={analytic.productsCount} growth={calcGrowth(analytic.productsCount, previous.productsCount)} />
                    <Subtitle caption={translate('analytics.product.avgPackagesPerTransaction')} value={analytic.packagesCount / device.transactionsCount} />
                    <Subtitle
                        caption={translate('analytics.product.avgProductsPerTransaction')}
                        value={analytic.transactionsCount / device.transactionsCount}
                    />
                </>
            }
            chart={
                <BarChartWithTrend
                    data={chartData}
                    dataKey='productsCount'
                    label={translate('analytics.product.productsCount')}
                    className='product-count-analytics-chart'
                />
            }
        />
    );
};
